import { INavigation, INavigationElement } from 'global';
import { FooterNavigationItem, INavigationData, NavigationItem } from 'interfaces/navigation-data';
import { PetType } from 'constants/pet-type';

export const NavigationType = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
} as const;

export const TargetGroup = {
  NEW_CUSTOMER: 'New Customer',
  EXISTING_CUSTOMER: 'Existing Customer',
} as const;

/**
 * Filters navigation elements based on type, customer type, and pet type
 */
const filterNavigationElement = (
  elements: INavigationElement[],
  type: (typeof NavigationType)[keyof typeof NavigationType],
  targetGroup: (typeof TargetGroup)[keyof typeof TargetGroup],
  petType: PetType
): INavigationElement | undefined => {
  return elements.find(
    (element) =>
      element?.fields?.type === type &&
      element?.fields?.targetGroup === targetGroup &&
      element?.fields?.petType.toLocaleLowerCase() === petType
  );
};

/**
 * Transforms a Contentful navigation structure into a two-dimensional array of NavigationItem objects.
 */
const transformNavigationStructure = (structure: Record<string, unknown>): NavigationItem[][] => {
  if (!structure) return [];

  // If it's already an array, return it as-is
  if (Array.isArray(structure)) {
    return structure as NavigationItem[][];
  }

  return [];
};

/**
 * Type guard to assert if an object matches the FooterNavigationItem structure
 */
export const isFooterNavigationItem = (item: unknown): item is FooterNavigationItem => {
  if (!item || typeof item !== 'object') return false;

  const potentialFooterItem = item as FooterNavigationItem;

  // Check if items array exists and is an array
  if (!Array.isArray(potentialFooterItem.items)) return false;

  // Check each top level item
  return potentialFooterItem.items.every((topLevelItem) => {
    if (!topLevelItem || typeof topLevelItem !== 'object') return false;
    if (!Array.isArray(topLevelItem.items)) return false;

    // Check each nested item
    return topLevelItem.items.every((nestedItem) => {
      if (!nestedItem || typeof nestedItem !== 'object') return false;

      // Verify required properties
      if (typeof nestedItem.title !== 'string') return false;
      if (typeof nestedItem.url !== 'string') return false;
      if (nestedItem.track && typeof nestedItem.track !== 'string') return false;

      return true;
    });
  });
};

/**
 * Creates navigation data from Contentful elements
 */
export const createNavigationFromContentful = ({
  elements,
  isExistingCustomer,
  dogTitle,
  catTitle,
  dogsHomePath,
  catsHomePath,
  footer,
  companyInfo,
  header,
}: {
  elements: INavigationElement[];
  isExistingCustomer: boolean;
  dogTitle: string;
  catTitle: string;
  dogsHomePath: string;
  catsHomePath: string;
  footer?: INavigationElement;
  companyInfo?: INavigationElement;
  header?: INavigationElement;
}): INavigationData => {
  const targetGroup = isExistingCustomer ? TargetGroup.EXISTING_CUSTOMER : TargetGroup.NEW_CUSTOMER;

  // Find mobile navigation for dogs and cats
  const mobileDogs = filterNavigationElement(
    elements,
    NavigationType.MOBILE,
    targetGroup,
    PetType.Dog
  );
  const mobileCats = filterNavigationElement(
    elements,
    NavigationType.MOBILE,
    targetGroup,
    PetType.Cat
  );

  // Find desktop navigation for dogs and cats
  const desktopDogs = filterNavigationElement(
    elements,
    NavigationType.DESKTOP,
    targetGroup,
    PetType.Dog
  );
  const desktopCats = filterNavigationElement(
    elements,
    NavigationType.DESKTOP,
    targetGroup,
    PetType.Cat
  );

  return {
    mobile: {
      dog: mobileDogs?.fields.structure
        ? transformNavigationStructure(mobileDogs.fields.structure)
        : [],
      cat: mobileCats?.fields.structure
        ? transformNavigationStructure(mobileCats.fields.structure)
        : [],
    },
    desktop: [
      ...(desktopDogs?.fields.structure
        ? [
            {
              title: dogTitle,
              context: PetType.Dog,
              url: dogsHomePath,
              items: transformNavigationStructure(
                desktopDogs.fields.structure
              ).flat() as NavigationItem[],
            },
          ]
        : []),
      ...(desktopCats?.fields.structure
        ? [
            {
              title: catTitle,
              context: PetType.Cat,
              url: catsHomePath,
              items: transformNavigationStructure(
                desktopCats.fields.structure
              ).flat() as NavigationItem[],
            },
          ]
        : []),
    ],
    footer:
      footer && isFooterNavigationItem(footer.fields.structure)
        ? footer.fields.structure
        : { items: [] },
    companyInfo:
      companyInfo && isFooterNavigationItem(companyInfo.fields.structure)
        ? companyInfo.fields.structure
        : { items: [] },
    header:
      header && isFooterNavigationItem(header.fields.structure)
        ? header.fields.structure
        : { items: [] },
  };
};

/**
 * Type guard to check if an object is a valid INavigation
 */
export const isNavigation = (obj: unknown): obj is INavigation => {
  if (!obj || typeof obj !== 'object') return false;

  const candidate = obj as Record<string, unknown>;

  if (!('fields' in candidate) || !candidate.fields) return false;

  if (typeof candidate.fields !== 'object' || candidate.fields === null) return false;

  const fields = candidate.fields as Record<string, unknown>;

  return 'element' in fields;
};
